// ===========================================
// BUSTED! BLACKJACK - CONNECTION STATUS COMPONENT
// ===========================================

import { useEffect, useState } from 'react';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { onReconnect } from '../stores/socketStore';
import { useSocketStore } from '../stores/socketStore';

// Animated dots for "reconnecting" text
function AnimatedDots() {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-block w-[12px] text-left">
      {'.'.repeat(dotCount)}
    </span>
  );
}

export function ConnectionStatus() {
  const { services, refresh } = useConnectionStatus();
  const { isConnected, isReconnecting } = useSocketStore();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Listen for browser online/offline events (instant detection)
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Refresh health check when socket reconnects
  useEffect(() => {
    const unsubscribe = onReconnect(() => {
      console.log('[ConnectionStatus] Socket reconnected, refreshing health check');
      refresh();
    });
    return unsubscribe;
  }, [refresh]);

  // When socket is not connected, show services as disconnected
  const effectiveServices = isConnected
    ? services
    : { database: 'error' as const, redis: 'error' as const, websocket: 'error' as const };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${
            isReconnecting ? 'bg-yellow-500 animate-pulse' :
            isConnected ? 'bg-green-500' : 'bg-red-500'
          }`} />
          Server Status
        </h2>
      </div>

      <div className="card-body space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-gray-400">BUSTED! Game Engine</span>
          <span className={`font-medium ${
            isReconnecting ? 'text-yellow-400' :
            isConnected ? 'text-green-400' : 'text-red-400'
          }`}>
            {isReconnecting ? <>Reconnecting<AnimatedDots /></> :
             isConnected ? 'Connected ✓' : 'Disconnected ✗'}
          </span>
        </div>
        
        <div className="border-t border-gray-700 pt-4 space-y-2">
          <h3 className="text-sm font-medium text-gray-400">Services</h3>

          <ServiceStatus name="Network" status={isOnline ? 'ok' : 'error'} />
          <ServiceStatus name="Database" status={effectiveServices.database} />
          <ServiceStatus name="Redis" status={effectiveServices.redis} />
          <ServiceStatus name="WebSocket" status={effectiveServices.websocket} />
        </div>
      </div>
    </div>
  );
}

function ServiceStatus({
  name,
  status
}: {
  name: string;
  status: 'ok' | 'error' | 'unknown';
}) {
  const statusColors = {
    ok: 'text-green-400',
    error: 'text-red-400',
    unknown: 'text-gray-500',
  };

  const statusIcons = {
    ok: '✓',
    error: '✗',
    unknown: '?',
  };

  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-300">{name}</span>
      <span className={statusColors[status]}>
        {statusIcons[status]} {status.toUpperCase()}
      </span>
    </div>
  );
}

export default ConnectionStatus;
